import { useResource } from "hooks/useResource";
import { getActivity } from "services/content";
import PageHero from "components/shared/PageHero";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";

/**
 * "Activity" — student life beyond the classroom: upcoming events, clubs &
 * co-curricular groups, and recent achievements. Content lives in the
 * `activity` blob (admin → Activity Page); photos stay on /gallery.
 */

function SectionHead({ kicker, title }) {
  return (
    <div className="mb-7">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-gold mb-1.5">
        {kicker}
      </div>
      <h2 className="font-serif text-[26px] sm:text-[30px] font-bold text-green-dark leading-tight">
        {title}
      </h2>
    </div>
  );
}

function EventCard({ ev, delay }) {
  // "15 Jan 2027" → big day number + the rest, falling back gracefully for
  // free-form dates ("TBA", "Every Thursday").
  const m = /^(\d{1,2})\s+(.+)$/.exec((ev.date || "").trim());
  return (
    <Reveal delay={delay}>
      <div className="h-full bg-white rounded-2xl border border-line p-5 flex gap-4 items-start shadow-[0_8px_24px_rgba(40,30,10,0.06)] hover:shadow-[0_14px_32px_rgba(40,30,10,0.12)] hover:-translate-y-0.5 transition-all duration-300">
        <div className="shrink-0 w-[64px] rounded-xl bg-green text-center py-2.5">
          {m ? (
            <>
              <div className="font-serif text-[24px] font-bold text-white leading-none">{m[1]}</div>
              <div className="text-[10.5px] font-bold uppercase tracking-wide text-[#c9e5d8] mt-1">{m[2]}</div>
            </>
          ) : (
            <div className="text-[11px] font-bold text-white px-1 py-2">{ev.date || "TBA"}</div>
          )}
        </div>
        <div className="min-w-0">
          <h3 className="font-serif text-[17px] font-bold text-ink leading-snug">{ev.title}</h3>
          <div className="text-[12px] text-muted mt-1">
            {[ev.time, ev.venue].filter(Boolean).join(" · ")}
          </div>
          {ev.desc && <p className="text-[13px] text-[#4a4a44] mt-1.5 leading-relaxed">{ev.desc}</p>}
        </div>
      </div>
    </Reveal>
  );
}

export default function ActivityPage() {
  const { data, loading } = useResource(getActivity);
  const events = data?.events || [];
  const clubs = data?.clubs || [];
  const achievements = data?.achievements || [];
  const empty = !loading && !events.length && !clubs.length && !achievements.length;

  return (
    <div className="animate-fadeIn">
      <PageHero
        title="Activities"
        sub={
          data?.hero?.sub ||
          "Events, clubs and achievements — student life beyond the classroom."
        }
        page="Activity"
      />

      <div className="max-w-container mx-auto px-4 sm:px-10 py-14">
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="rounded-2xl" style={{ height: 130 }} />
            ))}
          </div>
        )}

        {empty && (
          <p className="text-muted text-[15px]">
            Events, clubs and achievements will appear here as they are published.
          </p>
        )}

        {/* Upcoming events */}
        {events.length > 0 && (
          <section className="mb-16">
            <SectionHead kicker="Mark the date" title="Upcoming Events" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
              {events.map((ev, i) => (
                <EventCard key={`${ev.title}-${i}`} ev={ev} delay={i * 80} />
              ))}
            </div>
          </section>
        )}

        {/* Clubs & co-curricular */}
        {clubs.length > 0 && (
          <section className="mb-16">
            <SectionHead kicker="Beyond the classroom" title="Clubs & Co-curricular" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {clubs.map((c, i) => (
                <Reveal key={`${c.name}-${i}`} delay={(i % 3) * 80}>
                  <div
                    className="h-full bg-white rounded-2xl border border-line p-5 shadow-[0_8px_24px_rgba(40,30,10,0.06)] hover:shadow-[0_14px_32px_rgba(40,30,10,0.12)] hover:-translate-y-0.5 transition-all duration-300 border-t-[3px]"
                    style={{ borderTopColor: c.color || "#005C40" }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center text-[20px]"
                        style={{ background: `${c.color || "#005C40"}14` }}
                        aria-hidden="true"
                      >
                        {c.icon || "⭐"}
                      </div>
                      <h3 className="font-serif text-[17px] font-bold text-ink">{c.name}</h3>
                    </div>
                    {c.desc && (
                      <p className="text-[13px] text-[#4a4a44] leading-relaxed">{c.desc}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* Achievements */}
        {achievements.length > 0 && (
          <section>
            <SectionHead kicker="Proud moments" title="Recent Achievements" />
            <div className="flex flex-col gap-3 max-w-[760px]">
              {achievements.map((a, i) => (
                <Reveal key={`${a.title}-${i}`} delay={i * 80}>
                  <div className="bg-white rounded-2xl border border-line p-5 flex gap-4 items-start shadow-[0_8px_24px_rgba(40,30,10,0.06)]">
                    <div className="shrink-0 font-serif text-[18px] font-bold text-gold leading-none pt-0.5">
                      {a.year}
                    </div>
                    <div>
                      <h3 className="font-serif text-[16.5px] font-bold text-ink leading-snug">
                        {a.title}
                      </h3>
                      {a.detail && (
                        <p className="text-[13px] text-[#4a4a44] mt-1 leading-relaxed">{a.detail}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
